
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { generateIdeas } from "@/lib/api";
import { Idea } from "@/components/IdeaCard";
import { useAuth } from "@/contexts/AuthContext";
import { historyService } from "@/lib/historyUtils";

export function useResults(resultId: string | undefined) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [image, setImage] = useState<string | null>(null);
  
  useEffect(() => {
    let isMounted = true;
    
    async function loadResults() {
      console.log("loadResults start", { hasUser: !!user, resultId });
      try {
        setLoading(true);
        
        // Check if this is an explicit generation request
        const searchParams = new URLSearchParams(location.search);
        const shouldGenerate = searchParams.get('generate') === 'true';
        
        if (user) {
          // Authenticated user: check database first
          const userHistory = await historyService.getUserHistory(user.id);
          const historyItem = userHistory.find(item => item.id === resultId);
          
          if (historyItem) {
            // History item exists in database
            if (isMounted) {
              console.log("Loading ideas from database history:", historyItem.ideas);
              setIdeas(historyItem.ideas);
              setImage(historyItem.imageUrl);
            }
            return;
          }
        }
        
        // Fallback: Check localStorage (for new generations or non-authenticated users)
        const historyJson = localStorage.getItem("createJoyHistory");
        const history = historyJson ? JSON.parse(historyJson) : [];
        const historyItem = history.find((item: any) => item.id === resultId);
        
        if (historyItem) {
          // History item exists in localStorage
          if (isMounted) {
            console.log("Loading ideas from localStorage history:", historyItem.ideas);
            setIdeas(historyItem.ideas);
            setImage(historyItem.imageUrl);
          }
        } else if (shouldGenerate) {
          // Only generate new ideas if explicitly requested via generation flag AND image exists
          const savedImage = localStorage.getItem("capturedImage");
          if (!savedImage) {
            throw new Error("No image found");
          }
          
          // Double safety check: only generate if both conditions are met
          if (!shouldGenerate || !savedImage) {
            throw new Error("Invalid generation request");
          }
          
          setImage(savedImage);
          
          // Generate ideas
          const generatedIdeas = await generateIdeas(savedImage);
          
          if (isMounted) {
            console.log("Generated ideas:", generatedIdeas);
            
            if (generatedIdeas && generatedIdeas.length > 0) {
              setIdeas(generatedIdeas);
              // Save to appropriate storage
              await saveToHistory(resultId!, savedImage, generatedIdeas);
              
              // CRITICAL: Clear the captured image to prevent re-generation
              localStorage.removeItem("capturedImage");
              localStorage.setItem("imageProcessed", "true");
              console.log("🚫 Cleared capturedImage - generation complete");
            } else {
              throw new Error("No ideas were generated");
            }
          }
        } else {
          // No history item found and no generation flag - show error without navigation
          if (isMounted) {
            console.log("No history found for resultId:", resultId);
            throw new Error("Result not found");
          }
        }
      } catch (error) {
        console.error("Error loading results:", error);
        if (isMounted) {
          toast.error("Could not load ideas. Please try again.");
          setTimeout(() => {
            if (isMounted) navigate("/camera");
          }, 2000);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    
    loadResults();
    
    return () => {
      isMounted = false;
    };
  }, [resultId, navigate, user, location.search]);
  
  const saveToHistory = async (id: string, imageUrl: string, ideas: Idea[]) => {
    try {
      if (user) {
        // Save to database for authenticated users
        console.log("💾 Saving to database for authenticated user");
        await historyService.saveHistoryEntry(user.id, imageUrl, ideas);
      } else {
        // Fallback to localStorage for non-authenticated users
        console.log("💾 Saving to localStorage for non-authenticated user");
        const historyJson = localStorage.getItem("createJoyHistory");
        const history = historyJson ? JSON.parse(historyJson) : [];
        
        const existingIndex = history.findIndex((item: any) => item.id === id);
        const historyItem = {
          id,
          imageUrl,
          ideas,
          timestamp: new Date().toISOString(),
        };
        
        if (existingIndex >= 0) {
          history[existingIndex] = historyItem;
        } else {
          history.unshift(historyItem);
        }
        
        localStorage.setItem("createJoyHistory", JSON.stringify(history));
      }
    } catch (error) {
      console.error("Error saving to history:", error);
      // Fallback to localStorage even for authenticated users if database fails
      if (user) {
        console.log("🔄 Database save failed, falling back to localStorage");
        try {
          const historyJson = localStorage.getItem("createJoyHistory");
          const history = historyJson ? JSON.parse(historyJson) : [];
          
          const historyItem = {
            id,
            imageUrl,
            ideas,
            timestamp: new Date().toISOString(),
          };
          
          history.unshift(historyItem);
          localStorage.setItem("createJoyHistory", JSON.stringify(history));
        } catch (fallbackError) {
          console.error("Even localStorage fallback failed:", fallbackError);
        }
      }
    }
  };
  
  const handleSaveIdea = async (idea: Idea) => {
    try {
      if (user) {
        // Save to database for authenticated users
        console.log("💾 Saving idea to database for authenticated user");
        await historyService.saveIdea(user.id, idea);
        toast.success("Idea saved successfully");
      } else {
        // Fallback to localStorage for non-authenticated users
        console.log("💾 Saving idea to localStorage for non-authenticated user");
        const savedIdeasJson = localStorage.getItem("savedIdeas");
        const savedIdeas = savedIdeasJson ? JSON.parse(savedIdeasJson) : [];
        
        if (savedIdeas.some((saved: Idea) => saved.id === idea.id)) {
          toast("This idea is already saved");
          return;
        }
        
        savedIdeas.unshift(idea);
        localStorage.setItem("savedIdeas", JSON.stringify(savedIdeas));
        toast.success("Idea saved successfully");
      }
    } catch (error) {
      console.error("Error saving idea:", error);
      
      // Fallback to localStorage even for authenticated users if database fails
      if (user) {
        console.log("🔄 Database save failed, falling back to localStorage");
        try {
          const savedIdeasJson = localStorage.getItem("savedIdeas");
          const savedIdeas = savedIdeasJson ? JSON.parse(savedIdeasJson) : [];
          
          if (!savedIdeas.some((saved: Idea) => saved.id === idea.id)) {
            savedIdeas.unshift(idea);
            localStorage.setItem("savedIdeas", JSON.stringify(savedIdeas));
            toast.success("Idea saved successfully (offline)");
          } else {
            toast("This idea is already saved");
          }
        } catch (fallbackError) {
          toast.error("Failed to save idea");
        }
      } else {
        toast.error("Failed to save idea");
      }
    }
  };
  
  return {
    loading,
    ideas,
    image,
    handleSaveIdea
  };
}
